/**
 * The column visibility a grid restores for a user who has SAVED a layout.
 *
 * Two sources: the consumer's code defaults
 * (`initialState.columns.columnVisibilityModel`) and the user's saved model.
 * The saved model is MUI's exported state, so it carries every column that
 * was hidden when it was saved; a column it does not mention was VISIBLE then.
 *
 * The defaults exist for one case (ANX-1518): a column added hidden-by-default
 * after a user saved must stay hidden for them, not appear because their old
 * model never heard of it. So a default applies only to a column the saved
 * layout never saw — one missing from its `orderedFields`. A column the saved
 * layout already knew keeps the user's visibility, even when the code default
 * for it later changes (H253: the Flights list's defaults moved to Pierre
 * Marcoux's layout, and a user who had saved before must keep theirs).
 *
 * A saved layout with no `orderedFields` (an older shape) cannot say which
 * columns it knew, so it keeps the previous rule: defaults under saved.
 */
export function savedColumnVisibility(
  defaults: Record<string, boolean>,
  saved: Record<string, boolean> | undefined,
  savedOrderedFields: string[] | undefined
): Record<string, boolean> {
  const savedModel = saved || {};
  if (!Array.isArray(savedOrderedFields) || savedOrderedFields.length === 0) {
    return { ...defaults, ...savedModel };
  }
  const known = new Set(savedOrderedFields);
  const unseenDefaults = Object.fromEntries(
    Object.entries(defaults).filter(([field]) => !known.has(field))
  );
  return { ...unseenDefaults, ...savedModel };
}
